'use client'

import { ReactNode, useCallback, useState } from 'react'

import { MobileNavigationContext } from '@maestro/commerce-blocks/providers/mobile-navigation/context'
import { theme } from '@maestro/local/theme'
import {
  getElementStylePropertyValue,
  setDefaultMetaThemeColor,
  setMetaThemeColor,
} from '@pearlconvert/ui'
import { useDisabledPageScroll } from '@pearlconvert/ui/providers/disabled-page-scroll/disabled-page-scroll.context'

const MOBILE_MENU_THEME_COLOR = '--nav-mobile__header-background-color'

export const MobileNavigationProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const { toggleDisabledPageScroll } = useDisabledPageScroll()
  const [isOpen, setIsOpen] = useState(false)
  const [activeNodeIds, setActiveNodeIds] = useState([
    theme().cms.navigation.header,
  ])

  const openMenu = useCallback(() => {
    setActiveNodeIds([theme().cms.navigation.header])
    setIsOpen(true)
    toggleDisabledPageScroll(true)

    setMetaThemeColor(
      getElementStylePropertyValue(
        document.documentElement,
        MOBILE_MENU_THEME_COLOR
      )
    )
  }, [toggleDisabledPageScroll])

  const closeMenu = useCallback(() => {
    setIsOpen(false)
    toggleDisabledPageScroll(false)

    setDefaultMetaThemeColor()
  }, [toggleDisabledPageScroll])

  const openLevel = useCallback((id: string) => {
    setActiveNodeIds((prev) => [...prev, id])
  }, [])

  const openLevelAt = useCallback((parentIndex: number, id: string) => {
    setActiveNodeIds((prev) => [...prev.slice(0, parentIndex + 1), id])
  }, [])

  const closeLevel = useCallback(() => {
    if (activeNodeIds.length === 1) {
      return
    }
    setActiveNodeIds((prev) => prev.slice(0, -1))
  }, [activeNodeIds])

  const isActive = useCallback(
    (id: string) => {
      return activeNodeIds[activeNodeIds.length - 1] === id
    },
    [activeNodeIds]
  )

  const isChildActive = useCallback(
    (id: string) => {
      const activeNodes = activeNodeIds.slice(0, -1)
      return !!activeNodes.find((nodeId) => nodeId === id)
    },
    [activeNodeIds]
  )

  return (
    <MobileNavigationContext.Provider
      value={{
        isOpen,
        openMenu,
        closeMenu,
        activeNodeIds,
        openLevel,
        openLevelAt,
        closeLevel,
        isActive,
        isChildActive,
      }}
    >
      {children}
    </MobileNavigationContext.Provider>
  )
}
